import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/SectionHeader';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SEOHead } from '../components/SEOHead';
import { blogArticles } from '../data/blogArticles';
import { Clock, ArrowRight, BookOpen, Calendar } from 'lucide-react';

const Blog = () => {
  const categories = [...new Set(blogArticles.map(a => a.category))];

  return (
    <div className="min-h-screen" data-testid="blog-page">
      <SEOHead />
      
      <PageHeader 
        title="Blog NFL"
        subtitle="Artículos completos y guías sobre fútbol americano en español"
        backgroundImage="https://images.unsplash.com/photo-1565789426992-9c335aa0a62c?auto=format&fit=crop&w=2000&q=80"
      />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: 'Blog' }]} />
        
        {/* Ad Space Placeholder - Top */}
        {/* <div className="mb-12 p-4 border-2 border-dashed border-slate-700 rounded-xl text-center text-slate-500 text-sm">
          <span className="opacity-50">Espacio publicitario</span>
        </div> */}

        {/* Featured Article */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-sm font-bold uppercase rounded-full">
              Destacado
            </span>
          </div>
          
          <Link 
            to={`/blog/${blogArticles[0].slug}`}
            className="group block relative overflow-hidden rounded-2xl"
          >
            <div 
              className="aspect-[21/9] bg-cover bg-center"
              style={{ backgroundImage: `url(${blogArticles[0].image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span 
                className="inline-block px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-bold uppercase rounded mb-4"
              >
                {blogArticles[0].category}
              </span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-white mb-3 group-hover:text-emerald-400 transition-colors"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {blogArticles[0].title}
              </h2>
              <p className="text-slate-300 text-lg mb-4 max-w-3xl">
                {blogArticles[0].excerpt}
              </p>
              <div className="flex items-center gap-6 text-slate-400 text-sm">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {blogArticles[0].readTime}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(blogArticles[0].publishDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>
          </Link>
        </section>

        {/* All Articles */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 
              className="text-2xl md:text-3xl font-bold text-white uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Todos los Artículos
            </h2>
            <div className="flex gap-2">
              {categories.map(cat => (
                <span 
                  key={cat}
                  className="px-3 py-1 bg-slate-800 text-slate-400 text-xs font-medium rounded-full"
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogArticles.map((article, index) => (
              <article 
                key={article.id}
                className="group"
                itemScope 
                itemType="https://schema.org/BlogPosting"
              >
                <Link to={`/blog/${article.slug}`} className="block">
                  <div className="relative overflow-hidden rounded-xl mb-4">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
                      itemProp="image"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-slate-900/80 text-emerald-400 text-xs font-bold rounded">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  
                  <h3 
                    className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors line-clamp-2"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    itemProp="headline"
                  >
                    {article.title}
                  </h3>
                  
                  <p 
                    className="text-slate-400 text-sm mb-3 line-clamp-2"
                    itemProp="description"
                  >
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-slate-500 text-xs">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {article.readTime}
                    </span>
                    <span 
                      className="flex items-center gap-1 text-emerald-400 group-hover:gap-2 transition-all"
                    >
                      Leer más
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                  
                  <meta itemProp="datePublished" content={article.publishDate} />
                </Link>
              </article>
            ))}
          </div>
        </section>

        {/* Ad Space Placeholder - Bottom */}
        {/* <div className="mt-16 p-4 border-2 border-dashed border-slate-700 rounded-xl text-center text-slate-500 text-sm">
          <span className="opacity-50">Espacio publicitario</span>
        </div> */}

        {/* CTA to Guide */}
        <section className="mt-16 bg-slate-800/50 rounded-2xl p-8 text-center">
          <BookOpen className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
          <h2 
            className="text-2xl font-bold text-white mb-3"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            ¿Prefieres una guía interactiva?
          </h2>
          <p className="text-slate-400 mb-6 max-w-lg mx-auto">
            Explora nuestra guía visual con diagramas de campo, posiciones y formaciones interactivas.
          </p>
          <Link 
            to="/que-es-nfl"
            className="btn-primary inline-flex items-center gap-2"
          >
            Ir a la Guía Visual
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Blog;
